import * as Cesium from "cesium";

import {
  buildEndPointEntityId,
  buildLabelEntityId,
  buildLineEntityId,
  buildStartPointEntityId,
  ENTITY_PREFIX,
  MEASUREMENT_LINE_DEPTH_FAIL_MATERIAL,
  MEASUREMENT_LINE_MATERIAL,
  PENDING_POINT_ENTITY_ID,
  PREVIEW_LABEL_ENTITY_ID,
  PREVIEW_LINE_DEPTH_FAIL_MATERIAL,
  PREVIEW_LINE_ENTITY_ID,
  PREVIEW_LINE_MATERIAL,
} from "./measurementConstants";
import {
  deserializePoint,
  type SerializedMeasurementPoint,
  type ViewerMeasurement,
} from "@/stores/measurementStore";

export const removeMeasurementEntities = (viewer: Cesium.Viewer) => {
  const entityIds = viewer.entities.values
    .map((entity) => entity.id)
    .filter((entityId): entityId is string =>
      typeof entityId === "string" ? entityId.startsWith(ENTITY_PREFIX) : false,
    );

  entityIds.forEach((entityId) => {
    const entity = viewer.entities.getById(entityId);
    if (entity) {
      viewer.entities.remove(entity);
    }
  });
};

export const addMeasurementEntities = (
  viewer: Cesium.Viewer,
  measurement: ViewerMeasurement,
) => {
  const startPoint = deserializePoint(measurement.startPoint);
  const endPoint = deserializePoint(measurement.endPoint);
  const midPoint = Cesium.Cartesian3.midpoint(
    startPoint,
    endPoint,
    new Cesium.Cartesian3(),
  );

  viewer.entities.add({
    id: buildLineEntityId(measurement.id),
    polyline: {
      positions: [startPoint, endPoint],
      width: 3,
      material: MEASUREMENT_LINE_MATERIAL,
      // Assure la visibilité de la ligne même si un modèle 3D la masque.
      depthFailMaterial: MEASUREMENT_LINE_DEPTH_FAIL_MATERIAL,
      clampToGround: measurement.type === "distance-ground",
    },
  });

  viewer.entities.add({
    id: buildStartPointEntityId(measurement.id),
    position: startPoint,
    point: {
      pixelSize: 8,
      color: Cesium.Color.WHITE,
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
  });

  viewer.entities.add({
    id: buildEndPointEntityId(measurement.id),
    position: endPoint,
    point: {
      pixelSize: 8,
      color: Cesium.Color.WHITE,
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
  });

  viewer.entities.add({
    id: buildLabelEntityId(measurement.id),
    position: midPoint,
    label: {
      text: measurement.distanceLabel,
      font: "12px sans-serif",
      fillColor: Cesium.Color.WHITE,
      showBackground: true,
      backgroundColor: Cesium.Color.BLACK.withAlpha(0.75),
      pixelOffset: new Cesium.Cartesian2(0, -14),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
  });
};

export const updateMeasurementEntities = (
  viewer: Cesium.Viewer,
  payload: {
    measurementId: string;
    measurementType: "distance-ground" | "height-from-ground";
    startPoint: Cesium.Cartesian3;
    endPoint: Cesium.Cartesian3;
    distanceLabel: string;
  },
) => {
  const midPoint = Cesium.Cartesian3.midpoint(
    payload.startPoint,
    payload.endPoint,
    new Cesium.Cartesian3(),
  );

  const line = viewer.entities.getById(buildLineEntityId(payload.measurementId));
  if (line?.polyline) {
    const linePositions = line.polyline.positions;
    if (linePositions instanceof Cesium.ConstantProperty) {
      linePositions.setValue([payload.startPoint, payload.endPoint]);
    } else {
      line.polyline.positions = new Cesium.ConstantProperty([
        payload.startPoint,
        payload.endPoint,
      ]);
    }
    line.polyline.depthFailMaterial = MEASUREMENT_LINE_DEPTH_FAIL_MATERIAL;
    const clampToGround = line.polyline.clampToGround;
    if (clampToGround instanceof Cesium.ConstantProperty) {
      clampToGround.setValue(payload.measurementType === "distance-ground");
    } else {
      line.polyline.clampToGround = new Cesium.ConstantProperty(
        payload.measurementType === "distance-ground",
      );
    }
  }

  const startPointEntity = viewer.entities.getById(
    buildStartPointEntityId(payload.measurementId),
  );
  if (startPointEntity) {
    if (startPointEntity.position instanceof Cesium.ConstantPositionProperty) {
      startPointEntity.position.setValue(payload.startPoint);
    } else {
      startPointEntity.position = new Cesium.ConstantPositionProperty(
        payload.startPoint,
      );
    }
  }

  const endPointEntity = viewer.entities.getById(
    buildEndPointEntityId(payload.measurementId),
  );
  if (endPointEntity) {
    if (endPointEntity.position instanceof Cesium.ConstantPositionProperty) {
      endPointEntity.position.setValue(payload.endPoint);
    } else {
      endPointEntity.position = new Cesium.ConstantPositionProperty(
        payload.endPoint,
      );
    }
  }

  const label = viewer.entities.getById(buildLabelEntityId(payload.measurementId));
  if (label?.label) {
    if (label.position instanceof Cesium.ConstantPositionProperty) {
      label.position.setValue(midPoint);
    } else {
      label.position = new Cesium.ConstantPositionProperty(midPoint);
    }

    if (label.label.text instanceof Cesium.ConstantProperty) {
      label.label.text.setValue(payload.distanceLabel);
    } else {
      label.label.text = new Cesium.ConstantProperty(payload.distanceLabel);
    }
  }
};

export const upsertPreviewEntities = (
  viewer: Cesium.Viewer,
  payload: {
    startPoint: Cesium.Cartesian3;
    endPoint: Cesium.Cartesian3;
    label: string;
    clampToGround: boolean;
  },
) => {
  const midPoint = Cesium.Cartesian3.midpoint(
    payload.startPoint,
    payload.endPoint,
    new Cesium.Cartesian3(),
  );

  const existingLine = viewer.entities.getById(PREVIEW_LINE_ENTITY_ID);
  if (existingLine?.polyline) {
    const positions = existingLine.polyline.positions;
    if (positions instanceof Cesium.ConstantProperty) {
      positions.setValue([payload.startPoint, payload.endPoint]);
    } else {
      existingLine.polyline.positions = new Cesium.ConstantProperty([
        payload.startPoint,
        payload.endPoint,
      ]);
    }
    existingLine.polyline.depthFailMaterial = PREVIEW_LINE_DEPTH_FAIL_MATERIAL;
    const clampToGround = existingLine.polyline.clampToGround;
    if (clampToGround instanceof Cesium.ConstantProperty) {
      clampToGround.setValue(payload.clampToGround);
    } else {
      existingLine.polyline.clampToGround = new Cesium.ConstantProperty(
        payload.clampToGround,
      );
    }
  } else {
    viewer.entities.add({
      id: PREVIEW_LINE_ENTITY_ID,
      polyline: {
        positions: [payload.startPoint, payload.endPoint],
        width: 2,
        material: PREVIEW_LINE_MATERIAL,
        depthFailMaterial: PREVIEW_LINE_DEPTH_FAIL_MATERIAL,
        clampToGround: payload.clampToGround,
      },
    });
  }

  const existingLabel = viewer.entities.getById(PREVIEW_LABEL_ENTITY_ID);
  if (existingLabel?.label) {
    if (existingLabel.position instanceof Cesium.ConstantPositionProperty) {
      existingLabel.position.setValue(midPoint);
    } else {
      existingLabel.position = new Cesium.ConstantPositionProperty(midPoint);
    }

    if (existingLabel.label.text instanceof Cesium.ConstantProperty) {
      existingLabel.label.text.setValue(payload.label);
    } else {
      existingLabel.label.text = new Cesium.ConstantProperty(payload.label);
    }
  } else {
    viewer.entities.add({
      id: PREVIEW_LABEL_ENTITY_ID,
      position: midPoint,
      label: {
        text: payload.label,
        font: "12px sans-serif",
        fillColor: Cesium.Color.WHITE,
        showBackground: true,
        backgroundColor: Cesium.Color.BLACK.withAlpha(0.75),
        pixelOffset: new Cesium.Cartesian2(0, -14),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
  }
};

export const removePreviewEntities = (viewer: Cesium.Viewer) => {
  if (viewer.isDestroyed()) return;

  const previewLine = viewer.entities.getById(PREVIEW_LINE_ENTITY_ID);
  if (previewLine) {
    viewer.entities.remove(previewLine);
  }

  const previewLabel = viewer.entities.getById(PREVIEW_LABEL_ENTITY_ID);
  if (previewLabel) {
    viewer.entities.remove(previewLabel);
  }
};

export const syncPendingPointEntity = (
  viewer: Cesium.Viewer,
  pendingFirstPoint: SerializedMeasurementPoint | null,
) => {
  const existingPendingPoint = viewer.entities.getById(PENDING_POINT_ENTITY_ID);

  if (!pendingFirstPoint) {
    if (existingPendingPoint) {
      viewer.entities.remove(existingPendingPoint);
    }
    return;
  }

  const position = deserializePoint(pendingFirstPoint);

  if (existingPendingPoint) {
    if (existingPendingPoint.position instanceof Cesium.ConstantPositionProperty) {
      existingPendingPoint.position.setValue(position);
    } else {
      existingPendingPoint.position = new Cesium.ConstantPositionProperty(position);
    }
    return;
  }

  viewer.entities.add({
    id: PENDING_POINT_ENTITY_ID,
    position,
    point: {
      pixelSize: 9,
      color: Cesium.Color.YELLOW,
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
  });
};
